import { memo, useCallback, useMemo, useState } from "react";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import BatchTaskSendStatusChip from "./BatchTaskSendStatusChip";
import CommunicationsBatchTaskDetailDialog from "./CommunicationsBatchTaskDetailDialog";
import CommunicationsBirthdayPatientsDialog from "./CommunicationsBirthdayPatientsDialog";
import {
  deriveBatchTaskSendStatus,
  formatBatchFailuresCount,
  formatBatchReceivedTotal,
} from "../helpers/formatBatchTaskDisplay";
import type { UseBirthdayDashboardStateResult } from "../hooks/useBirthdayDashboardState";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";

export interface BirthdayDashboardAlertProps {
  state: UseBirthdayDashboardStateResult;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

/** Separador visual entre trechos inline do alerta compacto. */
function InlineDot() {
  return (
    <Typography
      component="span"
      variant="caption"
      color="text.disabled"
      aria-hidden
      sx={{ userSelect: "none", lineHeight: 1 }}
    >
      ·
    </Typography>
  );
}

/**
 * Alerta de aniversariantes do dia no dashboard profissional.
 * Layout compacto em faixa inline; descrição longa fica em tooltip no título.
 */
const BirthdayDashboardAlert = memo(
  ({ state, onOpenSendMessage }: BirthdayDashboardAlertProps) => {
    const [birthdayDialogOpen, setBirthdayDialogOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    const {
      shouldShowAlert,
      todayBirthdays,
      todayBatchTask,
      dispatchStatusLabel,
      canTriggerDispatch,
      triggerDisabledReason,
      isTriggering,
      communications,
      allPatients,
      patientLookup,
      triggerDispatch,
    } = state;

    const countLabel = useMemo(() => {
      const n = todayBirthdays.length;
      if (n === 0) return "Nenhum aniversariante hoje";
      return n === 1 ? "1 aniversariante hoje" : `${n} aniversariantes hoje`;
    }, [todayBirthdays.length]);

    const titleTooltip = useMemo(() => {
      if (todayBirthdays.length === 0) {
        return "Não há aniversariantes hoje. Use Ver aniversariantes para consultar dia, semana ou mês e enviar mensagens individuais.";
      }
      return "Envie parabéns automaticamente com a automação Aniversariantes ou visualize a lista para mensagens individuais.";
    }, [todayBirthdays.length]);

    const sendStatusChip = useMemo(() => {
      if (!todayBatchTask) return null;
      return deriveBatchTaskSendStatus(todayBatchTask);
    }, [todayBatchTask]);

    const inlineMetrics = useMemo(() => {
      if (!todayBatchTask) return null;
      const received = formatBatchReceivedTotal(todayBatchTask);
      const failures = formatBatchFailuresCount(todayBatchTask);
      return { received, failures };
    }, [todayBatchTask]);

    const handleTrigger = useCallback(() => {
      void triggerDispatch();
    }, [triggerDispatch]);

    if (!shouldShowAlert) return null;

    const sendButton = (
      <Button
        variant="contained"
        size="small"
        disabled={!canTriggerDispatch}
        onClick={handleTrigger}
        startIcon={
          isTriggering ? (
            <CircularProgress size={16} color="inherit" />
          ) : undefined
        }
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        {isTriggering ? "Enviando…" : "Enviar mensagem"}
      </Button>
    );

    return (
      <>
        <Alert
          severity="info"
          icon={<CakeOutlinedIcon fontSize="small" />}
          sx={{
            borderRadius: 2,
            alignItems: "center",
            py: 0.75,
            "& .MuiAlert-message": { width: "100%", py: 0.25 },
            "& .MuiAlert-icon": { alignSelf: "center", py: 0 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ rowGap: 0.75 }}
          >
            <Tooltip title={titleTooltip} arrow>
              <Typography
                variant="body2"
                fontWeight={700}
                component="span"
                sx={{ cursor: "help", flexShrink: 0 }}
              >
                {countLabel}
              </Typography>
            </Tooltip>

            <InlineDot />

            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              sx={{ flexShrink: 0 }}
            >
              Disparo: {dispatchStatusLabel}
            </Typography>

            {sendStatusChip ? (
              <>
                <BatchTaskSendStatusChip status={sendStatusChip} size="small" />
              </>
            ) : null}

            {inlineMetrics ? (
              <>
                <InlineDot />
                <Typography variant="caption" color="text.secondary" component="span">
                  {inlineMetrics.received} recebidos
                </Typography>
                <Typography variant="caption" color="text.secondary" component="span">
                  {inlineMetrics.failures} falhas
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "1rem" }} />}
                  onClick={() => setDetailOpen(true)}
                  sx={{ textTransform: "none", minWidth: 0, px: 0.5, py: 0 }}
                >
                  Detalhes
                </Button>
              </>
            ) : null}

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                ml: { xs: 0, sm: "auto" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {triggerDisabledReason && !canTriggerDispatch ? (
                <Tooltip title={triggerDisabledReason}>
                  <Box component="span">{sendButton}</Box>
                </Tooltip>
              ) : (
                sendButton
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={() => setBirthdayDialogOpen(true)}
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                Ver aniversariantes
              </Button>
            </Box>
          </Stack>
        </Alert>

        {birthdayDialogOpen ? (
          <CommunicationsBirthdayPatientsDialog
            open={birthdayDialogOpen}
            onClose={() => setBirthdayDialogOpen(false)}
            allPatients={allPatients}
            communications={communications}
            onOpenSendMessage={onOpenSendMessage}
          />
        ) : null}

        {detailOpen && todayBatchTask ? (
          <CommunicationsBatchTaskDetailDialog
            open={detailOpen}
            task={todayBatchTask}
            patientLookup={patientLookup}
            onClose={() => setDetailOpen(false)}
            onOpenSendMessage={onOpenSendMessage}
          />
        ) : null}
      </>
    );
  },
);

BirthdayDashboardAlert.displayName = "BirthdayDashboardAlert";

export default BirthdayDashboardAlert;
